import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../providers/GlobalProvider';
import {
  ExportHubAPI,
  ExportLicense,
  ExportLicenseType,
  CreateLicenseInput,
} from 'api/ExportHubAPI';
import {
  LICENSE_TYPES,
  LICENSE_TYPE_DESCRIPTIONS,
  AGREEMENT_TYPES,
  BUSINESS_UNITS,
  BUSINESS_SEGMENTS,
  CW_ROLES,
  COUNTRIES,
} from './licensePresets';
import StyledSingleSelect from './StyledSingleSelect';
import { USML_CATEGORIES } from './jurisdictionFields';
import { Toggle, ChipInput } from './formControls';
import CreatableSelect from './CreatableSelect';

interface Props {
  open: boolean;
  databaseId: string;
  groupId: string | undefined;
  onClose: () => void;
  onCreated: (_license: ExportLicense) => void;
  /** When set, the license is auto-linked to this product once added. */
  lockedProductId?: string;
  lockedProductName?: string;
}

interface FormState {
  license_type: ExportLicenseType | '';
  business_unit: string;
  business_segment: string;
  internal_transaction_number: string;
  custodian: string;
  signatory: string;
  country: string;
  destination_country: string;
  customer: string;
  cw_role: string;
  foreign_end_user: string;
  agreement_owner: string;
  parties_to_agreement: string[];
  sublicensees: string[];
  foreign_parties: string[];
  eccns: string[];
  usml_category: string;
  program_name: string;
  program_description: string;
  quantity_unit: string;
  hardware_authorized: boolean;
  tech_data_authorized: boolean;
  proposed_value: string;
  comments: string;
}

const EMPTY: FormState = {
  license_type: '',
  business_unit: '',
  business_segment: '',
  internal_transaction_number: '',
  custodian: '',
  signatory: '',
  country: '',
  destination_country: '',
  customer: '',
  cw_role: '',
  foreign_end_user: '',
  agreement_owner: '',
  parties_to_agreement: [],
  sublicensees: [],
  foreign_parties: [],
  eccns: [],
  usml_category: '',
  program_name: '',
  program_description: '',
  quantity_unit: '',
  hardware_authorized: true,
  tech_data_authorized: false,
  proposed_value: '',
  comments: '',
};

const STEPS = [
  'Type & Routing',
  'Parties & Destination',
  'Items & Scope',
  'Review & Add',
];

// Split a "24 units" / "30/ea" style string into a numeric quantity + unit so
// the structured backend columns stay populated from the single design field.
function splitQuantity(raw: string): {
  quantity?: number;
  unit_of_measure?: string;
} {
  const s = raw.trim();
  if (!s) return {};
  const m = s.match(/^(\d+(?:\.\d+)?)\s*[/x×]?\s*(.*)$/i);
  if (!m) return { unit_of_measure: s };
  const quantity = Math.floor(Number(m[1]));
  const unit = m[2].trim();
  return {
    quantity: Number.isFinite(quantity) ? quantity : undefined,
    unit_of_measure: unit || undefined,
  };
}

const LicenseRequestWizard: React.FC<Props> = ({
  open,
  databaseId,
  groupId,
  onClose,
  onCreated,
  lockedProductId,
  lockedProductName,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState<ExportLicense | null>(null);

  const patch = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));
  const isAgreement =
    !!form.license_type &&
    AGREEMENT_TYPES.has(form.license_type as ExportLicenseType);
  // DSP-* and the agreement types are State/DDTC — classified by USML category
  // rather than ECCN (deriveAgency uses the same rule).
  const isItar =
    !!form.license_type &&
    (form.license_type.startsWith('DSP') ||
      AGREEMENT_TYPES.has(form.license_type as ExportLicenseType));

  // Team-member names seed the custodian / signatory selects; users can also
  // type a name that isn't on the team (CreatableSelect handles persistence).
  const memberNames = useMemo(() => {
    const raw = (cur_user?.selected_team?.members as any[]) || [];
    return raw
      .map(
        (m) =>
          `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email || '',
      )
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [cur_user]);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setStep(0);
    setSubmitted(null);
  }, [open]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const stepValid = useMemo(() => {
    if (step === 0) {
      return (
        !!form.license_type &&
        !!form.business_unit &&
        !!form.business_segment &&
        !!form.custodian.trim() &&
        !!form.signatory.trim()
      );
    }
    if (step === 1) {
      if (!form.country || !form.destination_country || !form.customer) {
        return false;
      }
      if (isAgreement && !form.agreement_owner.trim()) return false;
      return true;
    }
    if (step === 2) {
      // An ITAR licence is classified by its USML category; an EAR one by ECCN.
      const classified = isItar
        ? !!form.usml_category.trim()
        : form.eccns.length > 0;
      return (
        classified && !!form.program_name.trim() && !!form.proposed_value.trim()
      );
    }
    return true;
  }, [step, form, isAgreement, isItar]);

  const next = () => {
    if (!stepValid) {
      enqueueSnackbar('Please complete required fields before continuing.', {
        variant: 'warning',
      });
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const handleSubmit = async () => {
    if (!groupId) {
      enqueueSnackbar('No active team selected.', { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const { quantity, unit_of_measure } = splitQuantity(form.quantity_unit);
      const payload: CreateLicenseInput = {
        group_id: groupId,
        database_id: databaseId,
        product_id: lockedProductId || undefined,
        license_type: form.license_type as ExportLicenseType,
        business_unit: form.business_unit || undefined,
        business_segment: form.business_segment || undefined,
        internal_transaction_number:
          form.internal_transaction_number.trim() || undefined,
        license_custodian: form.custodian.trim() || undefined,
        signatory: form.signatory.trim() || undefined,
        country: form.country || undefined,
        country_of_ultimate_destination: form.destination_country || undefined,
        customer: form.customer.trim() || undefined,
        foreign_end_user: form.foreign_end_user.trim() || undefined,
        cw_role: form.cw_role || undefined,
        agreement_owner: isAgreement
          ? form.agreement_owner.trim() || undefined
          : undefined,
        cw_parties: isAgreement ? form.parties_to_agreement : undefined,
        sublicensees: isAgreement ? form.sublicensees : undefined,
        foreign_parties: isAgreement ? form.foreign_parties : undefined,
        eccns: form.eccns,
        usml_category: form.usml_category.trim() || undefined,
        program_name: form.program_name.trim() || undefined,
        program_description: form.program_description.trim() || undefined,
        quantity: quantity,
        unit_of_measure: unit_of_measure,
        hardware_authorized: form.hardware_authorized,
        tech_data_authorized: form.tech_data_authorized,
        proposed_value: form.proposed_value.trim()
          ? Math.max(0, Number(form.proposed_value))
          : undefined,
        comments: form.comments.trim() || undefined,
      };
      const license = await ExportHubAPI.requestLicense(payload);
      setSubmitted(license);
    } catch (err) {
      console.error('Failed to add license:', err);
      enqueueSnackbar('Failed to add license.', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Review rows (mirror the design's order). Agreement rows only when relevant.
  const reviewRows: Array<[string, string]> = [
    ['License Type', form.license_type || '—'],
    ['Business Unit', form.business_unit || '—'],
    ['Business Segment', form.business_segment || '—'],
    ['Internal Transaction #', form.internal_transaction_number || '—'],
    ['Custodian of Record', form.custodian || '—'],
    ['Signatory', form.signatory || '—'],
    ['Country', form.country || '—'],
    ['Destination', form.destination_country || '—'],
    ['Customer', form.customer || '—'],
    ['CW Role', form.cw_role || '—'],
    ['Foreign End User', form.foreign_end_user || '—'],
    ...((isAgreement
      ? [
          ['Agreement Owner', form.agreement_owner || '—'],
          [
            'Parties to Agreement',
            form.parties_to_agreement.join(', ') || '—',
          ],
          ['Sublicensees', form.sublicensees.join(', ') || '—'],
          ['Foreign Parties', form.foreign_parties.join(', ') || '—'],
        ]
      : []) as Array<[string, string]>),
    ...((isItar
      ? [['USML Category', form.usml_category || '—']]
      : [['ECCN(s)', form.eccns.join(', ') || '—']]) as Array<[string, string]>),
    ['Program Name', form.program_name || '—'],
    ['Quantity', form.quantity_unit || '—'],
    ['Program Description', form.program_description || '—'],
    ['Hardware Authorized', form.hardware_authorized ? 'Yes' : 'No'],
    ['Tech Data Authorized', form.tech_data_authorized ? 'Yes' : 'No'],
    [
      'Proposed Value',
      form.proposed_value
        ? `$${Number(form.proposed_value).toLocaleString()}`
        : '—',
    ],
  ];

  return (
    <Modal open={open} onClose={handleClose} className='eh-modal-root'>
      <div
        className='eh-modal eh-modal-wide eh-modal-wizard'
        role='dialog'
        aria-label='Add a license'
      >
        <div className='eh-modal-head'>
          <div>
            <h2 className='eh-modal-title'>Add a license</h2>
            {lockedProductName && (
              <p className='eh-modal-subtitle'>
                Auto-links to {lockedProductName} once added.
              </p>
            )}
          </div>
          <button
            className='eh-modal-close'
            onClick={handleClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        {submitted ? (
          <div className='eh-wizard-success'>
            <span className='eh-wizard-success-icon'>
              <CheckCircleOutlineIcon style={{ fontSize: 30 }} />
            </span>
            <h3 className='eh-wizard-success-title'>License added</h3>
            <p className='eh-wizard-success-sub'>
              It is now tracked with status <strong>Requested</strong>. Change
              the status on the license row if it has already been issued, and
              fill in the license number, dates and value once you have the
              issued document.
            </p>
            <div className='eh-wizard-success-actions'>
              <button
                className='btn-outline'
                type='button'
                onClick={() => {
                  setForm(EMPTY);
                  setStep(0);
                  setSubmitted(null);
                }}
              >
                Add another
              </button>
              <button
                className='btn-primary'
                type='button'
                onClick={() => onCreated(submitted)}
              >
                View in license table
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className='eh-stepper'>
              {STEPS.map((label, idx) => (
                <React.Fragment key={label}>
                  <div
                    className={`eh-step ${idx === step ? 'active' : ''} ${
                      idx < step ? 'done' : ''
                    }`}
                  >
                    <span className='eh-step-dot'>
                      {idx < step ? (
                        <CheckIcon style={{ fontSize: 14 }} />
                      ) : (
                        idx + 1
                      )}
                    </span>
                    <span className='eh-step-label'>{label}</span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <span
                      className={`eh-step-line ${idx < step ? 'done' : ''}`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className='eh-modal-body'>
              {/* Step 1 — Type & Routing */}
              {step === 0 && (
                <>
                  <div className='eh-field'>
                    <span className='eh-field-label'>
                      License type <span className='eh-req'>*</span>
                    </span>
                    <CreatableSelect
                      value={form.license_type}
                      onChange={(v) =>
                        patch({ license_type: v as ExportLicenseType })
                      }
                      options={LICENSE_TYPES as unknown as string[]}
                      allowCreate={false}
                      placeholder='Select license type'
                      describe={(o) =>
                        LICENSE_TYPE_DESCRIPTIONS[o as ExportLicenseType]
                      }
                      ariaLabel='License type'
                    />
                    <span className='eh-field-hint'>
                      Drives which fields appear in later steps.
                    </span>
                  </div>
                  <div className='eh-field-row'>
                    <div className='eh-field'>
                      <span className='eh-field-label'>
                        Business Unit <span className='eh-req'>*</span>
                      </span>
                      <CreatableSelect
                        value={form.business_unit}
                        onChange={(v) => patch({ business_unit: v })}
                        options={BUSINESS_UNITS}
                        field='business_unit'
                        groupId={groupId}
                        placeholder='Select or create'
                        ariaLabel='Business Unit'
                      />
                    </div>
                    <div className='eh-field'>
                      <span className='eh-field-label'>
                        Business Segment <span className='eh-req'>*</span>
                      </span>
                      <CreatableSelect
                        value={form.business_segment}
                        onChange={(v) => patch({ business_segment: v })}
                        options={BUSINESS_SEGMENTS}
                        field='business_segment'
                        groupId={groupId}
                        placeholder='Select or create'
                        ariaLabel='Business Segment'
                      />
                    </div>
                  </div>
                  <label className='eh-field'>
                    <span className='eh-field-label'>
                      Internal Transaction Number
                    </span>
                    <input
                      className='eh-input'
                      value={form.internal_transaction_number}
                      placeholder='e.g. ITN-2026-00451'
                      onChange={(e) =>
                        patch({ internal_transaction_number: e.target.value })
                      }
                    />
                  </label>
                  <div className='eh-field-row'>
                    <div className='eh-field'>
                      <span className='eh-field-label'>
                        License Custodian of Record{' '}
                        <span className='eh-req'>*</span>
                      </span>
                      <CreatableSelect
                        value={form.custodian}
                        onChange={(v) => patch({ custodian: v })}
                        options={memberNames}
                        field='custodian'
                        groupId={groupId}
                        placeholder='Select or add a name'
                        ariaLabel='License Custodian of Record'
                      />
                    </div>
                    <div className='eh-field'>
                      <span className='eh-field-label'>
                        Signatory / Empowered Official{' '}
                        <span className='eh-req'>*</span>
                      </span>
                      <CreatableSelect
                        value={form.signatory}
                        onChange={(v) => patch({ signatory: v })}
                        options={memberNames}
                        field='signatory'
                        groupId={groupId}
                        placeholder='Select or add a name'
                        ariaLabel='Signatory / Empowered Official'
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Step 2 — Parties & Destination */}
              {step === 1 && (
                <>
                  <div className='eh-field-row'>
                    <div className='eh-field'>
                      <span className='eh-field-label'>
                        Country <span className='eh-req'>*</span>
                      </span>
                      <CreatableSelect
                        value={form.country}
                        onChange={(v) => patch({ country: v })}
                        options={COUNTRIES}
                        field='country'
                        groupId={groupId}
                        placeholder='Source country'
                        ariaLabel='Country'
                      />
                    </div>
                    <div className='eh-field'>
                      <span className='eh-field-label'>
                        Country of Ultimate Destination{' '}
                        <span className='eh-req'>*</span>
                      </span>
                      <CreatableSelect
                        value={form.destination_country}
                        onChange={(v) => patch({ destination_country: v })}
                        options={COUNTRIES}
                        field='country'
                        groupId={groupId}
                        placeholder='Destination'
                        ariaLabel='Country of Ultimate Destination'
                      />
                    </div>
                  </div>
                  <div className='eh-field-row'>
                    <label className='eh-field'>
                      <span className='eh-field-label'>
                        Customer <span className='eh-req'>*</span>
                      </span>
                      <input
                        className='eh-input'
                        value={form.customer}
                        placeholder='e.g. Airbus Defence'
                        onChange={(e) => patch({ customer: e.target.value })}
                      />
                    </label>
                    <div className='eh-field'>
                      <span className='eh-field-label'>CW Role</span>
                      <CreatableSelect
                        value={form.cw_role}
                        onChange={(v) => patch({ cw_role: v })}
                        options={CW_ROLES}
                        field='cw_role'
                        groupId={groupId}
                        placeholder='Select or create'
                        ariaLabel='CW Role'
                      />
                    </div>
                  </div>
                  <label className='eh-field'>
                    <span className='eh-field-label'>Foreign End User</span>
                    <textarea
                      className='eh-input eh-textarea'
                      rows={2}
                      value={form.foreign_end_user}
                      placeholder='Name, address, role of the foreign end user'
                      onChange={(e) =>
                        patch({ foreign_end_user: e.target.value })
                      }
                    />
                  </label>

                  {isAgreement && (
                    <>
                      <div className='eh-section-divider'>
                        Agreement-specific details
                      </div>
                      <label className='eh-field'>
                        <span className='eh-field-label'>
                          Agreement Owner <span className='eh-req'>*</span>
                        </span>
                        <input
                          className='eh-input'
                          value={form.agreement_owner}
                          placeholder='Owner name'
                          onChange={(e) =>
                            patch({ agreement_owner: e.target.value })
                          }
                        />
                      </label>
                      <div className='eh-field'>
                        <span className='eh-field-label'>
                          Parties to Agreement
                        </span>
                        <ChipInput
                          values={form.parties_to_agreement}
                          onChange={(v) => patch({ parties_to_agreement: v })}
                          placeholder='Add party and press Enter'
                        />
                      </div>
                      <div className='eh-field'>
                        <span className='eh-field-label'>Sublicensees</span>
                        <ChipInput
                          values={form.sublicensees}
                          onChange={(v) => patch({ sublicensees: v })}
                          placeholder='Add sublicensee and press Enter'
                        />
                      </div>
                      <div className='eh-field'>
                        <span className='eh-field-label'>Foreign Parties</span>
                        <ChipInput
                          values={form.foreign_parties}
                          onChange={(v) => patch({ foreign_parties: v })}
                          placeholder='Add foreign party and press Enter'
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Step 3 — Items & Scope */}
              {step === 2 && (
                <>
                  {/* State/DDTC licences are filed against a USML category
                      (DSP-5 Block 11); Commerce/BIS ones against ECCNs. Show
                      the one that applies rather than asking for both. */}
                  {isItar ? (
                    <div className='eh-field'>
                      <span className='eh-field-label'>
                        USML Category <span className='eh-req'>*</span>
                      </span>
                      <StyledSingleSelect
                        options={[
                          { value: '', label: 'Select…' },
                          ...USML_CATEGORIES.map((c) => ({
                            value: c,
                            label: c,
                          })),
                        ]}
                        value={form.usml_category}
                        onChange={(v) => patch({ usml_category: v })}
                        inline
                      />
                      <span className='eh-field-hint'>
                        The category the defense articles on this license are
                        filed under. Per-part categories arrive with the
                        commodity line items.
                      </span>
                    </div>
                  ) : (
                    <div className='eh-field'>
                      <span className='eh-field-label'>
                        ECCN(s) <span className='eh-req'>*</span>
                      </span>
                      <ChipInput
                        values={form.eccns}
                        onChange={(v) => patch({ eccns: v })}
                        placeholder='e.g. 9A610, then Enter'
                      />
                    </div>
                  )}
                  <div className='eh-field-row'>
                    <label className='eh-field'>
                      <span className='eh-field-label'>
                        Program Name <span className='eh-req'>*</span>
                      </span>
                      <input
                        className='eh-input'
                        value={form.program_name}
                        placeholder='e.g. F-35 Avionics'
                        onChange={(e) =>
                          patch({ program_name: e.target.value })
                        }
                      />
                    </label>
                    <label className='eh-field'>
                      <span className='eh-field-label'>Quantity / Unit</span>
                      <input
                        className='eh-input'
                        value={form.quantity_unit}
                        placeholder='e.g. 24 units'
                        onChange={(e) =>
                          patch({ quantity_unit: e.target.value })
                        }
                      />
                    </label>
                  </div>
                  <label className='eh-field'>
                    <span className='eh-field-label'>
                      Program / Product / Tech Data Description
                    </span>
                    <textarea
                      className='eh-input eh-textarea'
                      rows={3}
                      value={form.program_description}
                      placeholder='Describe the items, technology, and intended use'
                      onChange={(e) =>
                        patch({ program_description: e.target.value })
                      }
                    />
                  </label>
                  <div className='eh-field-row'>
                    <div className='eh-field'>
                      <span className='eh-field-label'>Hardware Authorized?</span>
                      <Toggle
                        label={form.hardware_authorized ? 'Yes' : 'No'}
                        checked={form.hardware_authorized}
                        onChange={(v) => patch({ hardware_authorized: v })}
                      />
                    </div>
                    <div className='eh-field'>
                      <span className='eh-field-label'>
                        Tech Data Authorized?
                      </span>
                      <Toggle
                        label={form.tech_data_authorized ? 'Yes' : 'No'}
                        checked={form.tech_data_authorized}
                        onChange={(v) => patch({ tech_data_authorized: v })}
                      />
                    </div>
                  </div>
                  <label className='eh-field'>
                    <span className='eh-field-label'>
                      Proposed Value (USD) <span className='eh-req'>*</span>
                    </span>
                    <input
                      className='eh-input'
                      type='number'
                      min={0}
                      value={form.proposed_value}
                      placeholder='1000000'
                      onChange={(e) =>
                        patch({ proposed_value: e.target.value })
                      }
                    />
                  </label>
                </>
              )}

              {/* Step 4 — Review & Submit */}
              {step === 3 && (
                <>
                  <div className='eh-review'>
                    {reviewRows.map(([label, value]) => (
                      <div className='eh-review-row' key={label}>
                        <span className='eh-review-label'>{label}</span>
                        <span className='eh-review-value'>{value}</span>
                      </div>
                    ))}
                  </div>
                  <label className='eh-field' style={{ marginTop: 16 }}>
                    <span className='eh-field-label'>
                      Internal comments
                    </span>
                    <textarea
                      className='eh-input eh-textarea'
                      rows={3}
                      value={form.comments}
                      placeholder='Optional notes'
                      onChange={(e) => patch({ comments: e.target.value })}
                    />
                  </label>
                  <p className='eh-wizard-note'>
                    This becomes a tracked record with status{' '}
                    <strong>Requested</strong>. If the license has already been
                    issued, add it here and set its status on the license row —
                    license number, dates and value can be filled in at any time.
                  </p>
                </>
              )}
            </div>

            <div className='eh-modal-foot'>
              {step > 0 ? (
                <button
                  className='btn-outline'
                  type='button'
                  onClick={() => setStep((s) => s - 1)}
                  disabled={saving}
                >
                  Back
                </button>
              ) : (
                <button
                  className='btn-outline'
                  type='button'
                  onClick={handleClose}
                  disabled={saving}
                >
                  Cancel
                </button>
              )}
              <span className='eh-wizard-stepcount'>
                Step {step + 1} of {STEPS.length}
              </span>
              {step < STEPS.length - 1 ? (
                <button className='btn-primary' type='button' onClick={next}>
                  Continue
                </button>
              ) : (
                <button
                  className='btn-primary'
                  type='button'
                  onClick={handleSubmit}
                  disabled={saving}
                  style={{ opacity: saving ? 0.6 : 1 }}
                >
                  {saving ? 'Adding…' : 'Add license'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default LicenseRequestWizard;
